/**
 * MEVIA - Enneagram Company Dashboard
 * Phase 6: Admin Dashboard for Enneagram Statistics
 * 
 * Displays aggregated Enneagram data for company administrators:
 * - Type distribution chart
 * - Completion rates
 * - Department breakdown
 * - Insights and recommendations
 * - Legend with indicator explanations
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { 
  BarChart3, 
  Users, 
  TrendingUp, 
  Loader2,
  Info,
  ChevronDown,
  ChevronUp,
  Building,
  Target,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import logger from '../utils/logger';

// Enneagram type configurations
const ENNEAGRAM_TYPES = {
  1: { name: { es: 'El Perfeccionista', en: 'The Perfectionist' }, icon: '🎯', color: '#6366F1' },
  2: { name: { es: 'El Ayudador', en: 'The Helper' }, icon: '❤️', color: '#EC4899' },
  3: { name: { es: 'El Triunfador', en: 'The Achiever' }, icon: '🏆', color: '#F59E0B' },
  4: { name: { es: 'El Individualista', en: 'The Individualist' }, icon: '🎨', color: '#8B5CF6' },
  5: { name: { es: 'El Investigador', en: 'The Investigator' }, icon: '🔬', color: '#06B6D4' },
  6: { name: { es: 'El Leal', en: 'The Loyalist' }, icon: '🛡️', color: '#78716C' },
  7: { name: { es: 'El Entusiasta', en: 'The Enthusiast' }, icon: '🎉', color: '#22C55E' },
  8: { name: { es: 'El Desafiador', en: 'The Challenger' }, icon: '⚡', color: '#EF4444' },
  9: { name: { es: 'El Pacificador', en: 'The Peacemaker' }, icon: '☮️', color: '#64748B' }
};

const EnneagramDashboard = () => {
  const { language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLegend, setShowLegend] = useState(false);
  const [expandedDepartment, setExpandedDepartment] = useState(null);

  useEffect(() => {
    loadStats();
  }, [language]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/enneagram/company/stats?language=${language}`);
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      logger.error('Error loading Enneagram stats:', error);
      toast.error(language === 'es' ? 'Error al cargar estadísticas' : 'Error loading statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin mr-3" style={{ color: '#1A878D' }} />
          <span className="text-gray-500">
            {language === 'es' ? 'Cargando estadísticas del Eneagrama...' : 'Loading Enneagram statistics...'}
          </span>
        </CardContent>
      </Card>
    );
  }

  if (!stats || !stats.has_data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center text-lg">
            <span className="text-xl mr-2">🎭</span>
            {language === 'es' ? 'Dashboard de Eneagrama' : 'Enneagram Dashboard'}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center py-8">
          <span className="text-4xl block mb-4">🎭</span>
          <p className="text-gray-500 mb-2">
            {stats?.[`message_${language}`] || (language === 'es' 
              ? 'Aún no hay datos de Eneagrama disponibles' 
              : 'No Enneagram data available yet')}
          </p>
          <p className="text-sm text-gray-400">
            {language === 'es' 
              ? 'Los empleados pueden completar la evaluación desde su dashboard' 
              : 'Employees can complete the assessment from their dashboard'}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Card with Key Metrics */}
      <Card className="border-l-4" style={{ borderLeftColor: '#1A878D' }}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center text-lg">
              <span className="text-xl mr-2">🎭</span>
              {language === 'es' ? 'Dashboard de Eneagrama' : 'Enneagram Dashboard'}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={loadStats}>
              <RefreshCw className="h-4 w-4 mr-1" />
              {language === 'es' ? 'Actualizar' : 'Refresh'}
            </Button>
          </div>
          <CardDescription>
            {language === 'es' 
              ? 'Análisis de tipos de personalidad de tu organización' 
              : 'Personality type analysis of your organization'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Key Metrics Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-4 bg-teal-50 rounded-lg">
              <div className="text-3xl font-bold" style={{ color: '#1A878D' }}>
                {stats.total_completed}
              </div>
              <div className="text-sm text-gray-600">
                {language === 'es' ? 'Completadas' : 'Completed'}
              </div>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-3xl font-bold text-blue-600">
                {stats.total_employees}
              </div>
              <div className="text-sm text-gray-600">
                {language === 'es' ? 'Total Empleados' : 'Total Employees'}
              </div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-3xl font-bold text-green-600">
                {stats.completion_rate}%
              </div>
              <div className="text-sm text-gray-600">
                {language === 'es' ? 'Tasa Completado' : 'Completion Rate'}
              </div>
            </div>
            <div className="text-center p-4 rounded-lg" style={{ backgroundColor: `${stats.dominant_type?.color}15` }}>
              <div className="text-3xl">{stats.dominant_type?.icon}</div>
              <div className="text-sm text-gray-600">
                {language === 'es' ? 'Tipo Dominante' : 'Dominant Type'}
              </div>
            </div>
          </div>

          {/* Progress Bar for Completion */}
          <div className="mb-2">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">
                {language === 'es' ? 'Progreso de evaluaciones' : 'Assessment progress'}
              </span>
              <span className="font-medium">{stats.total_completed}/{stats.total_employees}</span>
            </div>
            <Progress value={stats.completion_rate} className="h-2" />
          </div>
        </CardContent>
      </Card>

      {/* Type Distribution Chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center text-base">
            <BarChart3 className="h-5 w-5 mr-2" style={{ color: '#1A878D' }} />
            {language === 'es' ? 'Distribución de Tipos' : 'Type Distribution'}
          </CardTitle>
          <CardDescription>
            {language === 'es' 
              ? 'Porcentaje de cada tipo de Eneagrama en tu organización' 
              : 'Percentage of each Enneagram type in your organization'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {stats.type_distribution.map((type) => (
              <div key={type.type} className="flex items-center gap-3">
                <div className="w-8 text-center text-xl">{type.icon}</div>
                <div className="w-24 md:w-32 text-sm truncate">
                  <span className="font-medium">Tipo {type.type}</span>
                  <span className="hidden md:inline text-gray-500 ml-1">
                    {type.name}
                  </span>
                </div>
                <div className="flex-1">
                  <div className="h-6 bg-gray-100 rounded-full overflow-hidden relative">
                    <div 
                      className="h-full rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                      style={{ 
                        width: `${Math.max(type.percentage, 5)}%`,
                        backgroundColor: type.color,
                        minWidth: type.count > 0 ? '40px' : '0'
                      }}
                    >
                      {type.count > 0 && (
                        <span className="text-xs font-medium text-white">
                          {type.count}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="w-14 text-right text-sm font-medium" style={{ color: type.color }}>
                  {type.percentage}%
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Insights Section */}
      {stats.insights && stats.insights.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center text-base">
              <TrendingUp className="h-5 w-5 mr-2" style={{ color: '#1A878D' }} />
              {language === 'es' ? 'Insights de tu Organización' : 'Organization Insights'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              {stats.insights.map((insight, index) => (
                <div 
                  key={index} 
                  className="p-4 rounded-lg border border-gray-200 bg-gray-50"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">{insight.icon}</span>
                    <h4 className="font-semibold text-sm">
                      {insight[`title_${language}`]}
                    </h4>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {insight[`description_${language}`]}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Department Breakdown */}
      {stats.by_department && stats.by_department.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center text-base">
              <Building className="h-5 w-5 mr-2" style={{ color: '#1A878D' }} />
              {language === 'es' ? 'Por Departamento' : 'By Department'}
            </CardTitle>
            <CardDescription>
              {language === 'es' 
                ? 'Distribución de tipos de Eneagrama por área' 
                : 'Enneagram type distribution by area'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.by_department.map((dept, index) => (
                <div key={index} className="border rounded-lg overflow-hidden">
                  <div 
                    className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => setExpandedDepartment(expandedDepartment === index ? null : index)}
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-medium">{dept.department}</span>
                      <Badge variant="secondary" className="text-xs">
                        {dept.total} {language === 'es' ? 'personas' : 'people'}
                      </Badge>
                      {dept.dominant_type && (
                        <span className="text-lg" title={`Tipo dominante: ${dept.dominant_type}`}>
                          {ENNEAGRAM_TYPES[dept.dominant_type]?.icon}
                        </span>
                      )}
                    </div>
                    {expandedDepartment === index ? (
                      <ChevronUp className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                  {expandedDepartment === index && (
                    <div className="p-3 border-t bg-white">
                      <div className="flex flex-wrap gap-2">
                        {dept.types.map((t) => (
                          <div 
                            key={t.type}
                            className="flex items-center gap-1 px-2 py-1 rounded-full text-sm"
                            style={{ backgroundColor: `${ENNEAGRAM_TYPES[t.type]?.color}20` }}
                          >
                            <span>{t.icon}</span>
                            <span className="font-medium">{t.count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Legend / Glossary Section */}
      <Card>
        <CardHeader className="pb-2">
          <div 
            className="flex items-center justify-between cursor-pointer"
            onClick={() => setShowLegend(!showLegend)}
          >
            <CardTitle className="flex items-center text-base">
              <Info className="h-5 w-5 mr-2" style={{ color: '#1A878D' }} />
              {language === 'es' ? 'Guía de Tipos de Eneagrama' : 'Enneagram Types Guide'}
            </CardTitle>
            {showLegend ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </div>
          <CardDescription>
            {language === 'es' 
              ? 'Descripción de cada tipo de personalidad y sus características' 
              : 'Description of each personality type and their characteristics'}
          </CardDescription>
        </CardHeader>
        {showLegend && (
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              {Object.entries(ENNEAGRAM_TYPES).map(([num, type]) => (
                <div 
                  key={num} 
                  className="p-3 rounded-lg border"
                  style={{ borderLeftWidth: '4px', borderLeftColor: type.color }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xl">{type.icon}</span>
                    <span className="font-semibold">Tipo {num}</span>
                  </div>
                  <p className="text-sm font-medium" style={{ color: type.color }}>
                    {type.name[language]}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {getTypeDescription(parseInt(num), language)}
                  </p>
                </div>
              ))}
            </div>
            
            {/* Additional Explanations */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-sm mb-3">
                {language === 'es' ? 'Interpretación de los Indicadores' : 'Indicator Interpretation'}
              </h4>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-medium text-gray-700">
                    {language === 'es' ? '📊 Distribución de Tipos' : '📊 Type Distribution'}
                  </p>
                  <p className="text-gray-600 text-xs">
                    {language === 'es' 
                      ? 'Muestra el porcentaje de empleados con cada tipo de Eneagrama. Una distribución equilibrada sugiere diversidad de perspectivas.' 
                      : 'Shows the percentage of employees with each Enneagram type. A balanced distribution suggests diversity of perspectives.'}
                  </p>
                </div>
                <div>
                  <p className="font-medium text-gray-700">
                    {language === 'es' ? '🏆 Tipo Dominante' : '🏆 Dominant Type'}
                  </p>
                  <p className="text-gray-600 text-xs">
                    {language === 'es' 
                      ? 'El tipo de personalidad más común en tu organización. Indica tendencias culturales y estilos de trabajo predominantes.' 
                      : 'The most common personality type in your organization. Indicates cultural trends and predominant work styles.'}
                  </p>
                </div>
                <div>
                  <p className="font-medium text-gray-700">
                    {language === 'es' ? '⚖️ Balance de Estilos' : '⚖️ Style Balance'}
                  </p>
                  <p className="text-gray-600 text-xs">
                    {language === 'es' 
                      ? 'Agrupa los tipos en tres centros: Acción (3,7,8), Relaciones (1,2,6), y Reflexión (4,5,9). Un balance ayuda a la toma de decisiones integral.' 
                      : 'Groups types into three centers: Action (3,7,8), Relationships (1,2,6), and Reflection (4,5,9). Balance helps with comprehensive decision-making.'}
                  </p>
                </div>
                <div>
                  <p className="font-medium text-gray-700">
                    {language === 'es' ? '🌈 Diversidad de Tipos' : '🌈 Type Diversity'}
                  </p>
                  <p className="text-gray-600 text-xs">
                    {language === 'es' 
                      ? 'Cantidad de tipos diferentes representados. Mayor diversidad (7-9 tipos) sugiere equipos más adaptables y creativos.' 
                      : 'Number of different types represented. Higher diversity (7-9 types) suggests more adaptable and creative teams.'}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

// Helper function for brief type descriptions
function getTypeDescription(type, language) {
  const descriptions = {
    1: {
      es: 'Ético, dedicado, confiable. Busca mejorar las cosas según sus ideales.',
      en: 'Ethical, dedicated, reliable. Seeks to improve things according to their ideals.'
    },
    2: {
      es: 'Generoso, emotivo, complaciente. Se enfoca en ayudar y conectar con otros.',
      en: 'Generous, emotional, people-pleasing. Focuses on helping and connecting with others.'
    },
    3: {
      es: 'Adaptable, ambicioso, impulsado. Orientado al éxito y la imagen.',
      en: 'Adaptable, ambitious, driven. Success and image-oriented.'
    },
    4: {
      es: 'Expresivo, dramático, ensimismado. Busca autenticidad y significado.',
      en: 'Expressive, dramatic, self-absorbed. Seeks authenticity and meaning.'
    },
    5: {
      es: 'Perceptivo, innovador, reservado. Analiza y acumula conocimiento.',
      en: 'Perceptive, innovative, secretive. Analyzes and accumulates knowledge.'
    },
    6: {
      es: 'Comprometido, responsable, ansioso. Busca seguridad y apoyo.',
      en: 'Committed, responsible, anxious. Seeks security and support.'
    },
    7: {
      es: 'Espontáneo, versátil, disperso. Busca experiencias y posibilidades.',
      en: 'Spontaneous, versatile, scattered. Seeks experiences and possibilities.'
    },
    8: {
      es: 'Seguro, decidido, confrontativo. Busca control e independencia.',
      en: 'Confident, decisive, confrontational. Seeks control and independence.'
    },
    9: {
      es: 'Receptivo, tranquilizador, complaciente. Busca paz y armonía.',
      en: 'Receptive, reassuring, agreeable. Seeks peace and harmony.'
    }
  };
  return descriptions[type]?.[language] || '';
}

export default EnneagramDashboard;
